import { Box, Flex, Grid, GridItem, Icon, LinkBox, LinkOverlay } from "@chakra-ui/react";
import moment from "moment";
import { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import {
  FaAward,
  FaBullhorn,
  FaCheck,
  FaClock,
  FaComment,
  FaEnvelope,
  FaQuestion,
  FaShareAlt,
  FaShoppingBasket,
  FaSignature,
  FaTasks,
  FaTimes,
  FaUserEdit,
} from "react-icons/fa";
import { NotificationDto, NotificationTypeDto, useMarkNotificationsAsViewedMutation } from "../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../common/auth/useLoggedInWithOrgContextState";
import { displayDateWithTime } from "../../common/formatting/displayDateWithTime";
import { momentLocaleFor } from "../../common/formatting/i18nFormatters";
import { Explanation } from "../../common/support/Explanation/Explanation";
import { getNotificationDescription } from "./get-notification-description";
import { getNotificationLink } from "./getNotificationLink";

interface Props {
  notification: NotificationDto;
  index: number;
}

export const Notification = ({ notification, index }: Props) => {
  const { t, i18n } = useTranslation();
  const momentLocale = momentLocaleFor(i18n.language);
  const [markAsViewed] = useMarkNotificationsAsViewedMutation();

  const authState = useLoggedInWithOrgContextState();

  const getIcon = (): ReactElement => {
    switch (notification.type) {
      case NotificationTypeDto.ContractDateReminder:
        return <Icon as={FaClock} w="18px" h="18px" color="smBackground" />;
      case NotificationTypeDto.ContractDocumentCompletedSigning:
      case NotificationTypeDto.ContractSignatureDeadlineExpired:
        return <Icon as={FaSignature} w="15px" h="15px" color="smBackground" />;
      case NotificationTypeDto.ContractSharedWithDepartment:
      case NotificationTypeDto.ContractSharedWithOrg:
      case NotificationTypeDto.ContractSharedWithPerson:
        return <Icon as={FaShareAlt} w="18px" h="18px" color="smBackground" />;
      case NotificationTypeDto.ContractMessageReceived:
        return <Icon as={FaComment} w="18px" h="18px" color="smBackground" />;
      case NotificationTypeDto.ContractTaskDelegated:
      case NotificationTypeDto.ContractTaskStatusUpdated:
      case NotificationTypeDto.ContractTaskCommentAdded:
      case NotificationTypeDto.ContractTaskDeleted:
      case NotificationTypeDto.ContractTaskDueDateUpdated:
      case NotificationTypeDto.ContractTaskDueDateReminder:
        return <Icon as={FaTasks} w="15px" h="15px" color="smBackground" />;
      case NotificationTypeDto.SourcingEventAnnouncement:
        return <Icon as={FaBullhorn} w="18px" h="18px" color="smBackground" />;
      case NotificationTypeDto.SourcingEventAwarded:
        return <Icon as={FaAward} w="19px" h="19px" color="smBackground" />;
      case NotificationTypeDto.SourcingEventNewQuestion:
      case NotificationTypeDto.SourcingEventQuestionStatusUpdated:
      case NotificationTypeDto.SeV2NewQuestion:
      case NotificationTypeDto.SeV2QuestionAnswered:
        return <Icon as={FaQuestion} w="17px" h="17px" color="smBackground" />;
      case NotificationTypeDto.SeV2ClarificationMessageToBuyer:
      case NotificationTypeDto.SeV2ClarificationMessageToSupplier:
        return <Icon as={FaComment} w="17px" h="17px" color="smBackground" />;
      case NotificationTypeDto.SeV2InformationPublished:
        return <Icon as={FaBullhorn} w="18px" h="18px" color="smBackground" />;
      case NotificationTypeDto.SeV2AwardLetterDispatched:
      case NotificationTypeDto.SeV2AwardDecisionRevoked:
        return <Icon as={FaAward} w="19px" h="19px" color="smBackground" />;
      case NotificationTypeDto.SeV2BidDeadlineReminder:
        return <Icon as={FaShoppingBasket} w="18px" h="18px" color="smBackground" />;
      case NotificationTypeDto.SeV2InvitedToTender:
      case NotificationTypeDto.SeV2OrganizationInvite:
        return <Icon as={FaAward} w="19px" h="19px" color="smBackground" />;
      case NotificationTypeDto.SeV2RequestNotQualified:
      case NotificationTypeDto.SeV2RequestNotSelected:
      case NotificationTypeDto.SeV2DpsAdmitted:
      case NotificationTypeDto.SeV2QualificationSchemeAdmitted:
      case NotificationTypeDto.SeV2QualificationSchemeRemoved:
      case NotificationTypeDto.SeV2DpsRemoved:
        return <Icon as={FaShoppingBasket} w="18px" h="18px" color="smBackground" />;
      case NotificationTypeDto.SeV2ResponsibleBuyerChanged:
        return <Icon as={FaUserEdit} w="17px" h="17px" color="smBackground" />;
      case NotificationTypeDto.SeV2EventChanged:
      case NotificationTypeDto.SeV2EventCancelled:
      case NotificationTypeDto.SeV2EventClosed:
      case NotificationTypeDto.SeV2DoffinPublished:
        return <Icon as={FaBullhorn} w="18px" h="18px" color="smBackground" />;
      case NotificationTypeDto.SeV2DoffinPublicationFailed:
        return <Icon as={FaTimes} w="18px" h="18px" color="smBackground" />;
      case NotificationTypeDto.SourcingEventNewQuestionComment:
        return <Icon as={FaComment} w="18px" h="18px" color="smBackground" />;
      case NotificationTypeDto.SourcingEventOrganizationInvite:
      case NotificationTypeDto.NegotiationInvite:
      case NotificationTypeDto.SeV2RoundPublished:
      case NotificationTypeDto.SeV2BidDelivered:
      case NotificationTypeDto.BidDeliveryDeadline:
        return <Icon as={FaShoppingBasket} w="18px" h="18px" color="smBackground" />;
      case NotificationTypeDto.BidDelivered:
        return <Icon as={FaEnvelope} w="18px" h="18px" color="smBackground" />;
      case NotificationTypeDto.SourcingEventCancelled:
        return <Icon as={FaTimes} w="18px" h="18px" color="smBackground" />;
      case NotificationTypeDto.SourcingEventClosed:
        return <Icon as={FaCheck} w="18px" h="18px" color="smBackground" />;
    }
  };

  const link = getNotificationLink(notification, authState.selectedOrg.id);
  return (
    <LinkBox
      cursor={"pointer"}
      _hover={{ bg: "smBackgroundTertiary" }}
      borderTopLeftRadius={index === 0 ? "10px" : "0px"}
      borderTopRightRadius={index === 0 ? "10px" : "0px"}
      onClick={() => {
        markAsViewed({
          markNotificationsRequest: {
            notificationIds: [notification.id],
          },
        });
      }}
    >
      <LinkOverlay href={link}>
        <Flex
          pt="10px"
          pb="10px"
          borderTopLeftRadius={index === 0 ? "10px" : "0px"}
          borderTopRightRadius={index === 0 ? "10px" : "0px"}
          p="20px"
        >
          <Grid
            w="100%"
            gridTemplateColumns={`70px 1fr 100px`}
            templateAreas={`
                "leftIcon desc rightIcon"
                "leftIcon time rightIcon"
                `}
          >
            <GridItem area={"leftIcon"} display={"flex"} alignItems={"center"}>
              <Flex
                w={"50px"}
                height={"50px"}
                borderRadius="50%"
                bg={"blue.500"}
                alignItems={"center"}
                justifyContent="center"
              >
                {getIcon()}
              </Flex>
            </GridItem>
            <GridItem area={"desc"}>{getNotificationDescription(notification, momentLocale)}</GridItem>
            <GridItem area={"time"}>
              <Flex>
                <Flex justifyContent={"center"} alignItems={"center"} pr="5px">
                  <Icon as={FaClock} w="15px" h="15px" color={notification.viewed ? "smMuted" : "blue.500"} mb="-2px" />
                </Flex>
                <Flex>
                  <Box color="smMuted">
                    <Explanation enabled={true} text={displayDateWithTime(notification.createdAt, t("at"))}>
                      <Box fontSize="sm" color={notification.viewed ? "smMuted" : "blue.500"}>
                        {moment(notification.createdAt).fromNow()}
                      </Box>
                    </Explanation>
                  </Box>
                </Flex>
              </Flex>
            </GridItem>
            <GridItem area={"rightIcon"} display="flex" justifyContent={"end"} alignItems={"center"}>
              {notification.viewed ? null : <Flex w={"15px"} height={"15px"} borderRadius="50%" bg={"blue.500"} />}
            </GridItem>
          </Grid>
        </Flex>
      </LinkOverlay>
    </LinkBox>
  );
};
